import { useState } from "react";

import { Grid, Button, TextField, FormLabel, IconButton } from "@deere/fuel-react";
import { ReactComponent as DeleteIcon } from "@deere/ux.brand-foundations/icons/delete.svg";

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useOktaAuth } from "@okta/okta-react";

import ModalSelectCountry from "../components/ModalSelectCountry";
import { getCatalog } from "../../../../services/survey.service";

const SelectionsTab = ({ setError, setPayloadData, payloadData, selectedSec, que }) => {
  const { authState } = useOktaAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    // Catalog is fetched only when "Load From > Country" is used; survey.service caches it across instances
    const addCountryOptions = async () => {
        handleClose();
        const accessToken = authState?.accessToken?.accessToken;
        if (!accessToken) return;
        try {
          const response = await getCatalog(accessToken, "country");
          setCountryOptions(response || []);
          setShowCountryModal(true);
        } catch (error) {
          setError({ show: true, message: "Unable to load the country list. Please try again." });
        }
    };

    const addCountriesAsOptions = (queCode, countryNames) => {
      setError({ show: false, message: "" });
      setPayloadData((prevState) => {
        const sectionIndex = prevState.sections.findIndex((section) => section.code === selectedSec);
        const newSections = [...prevState.sections];
        const newQuestions = [...newSections[sectionIndex].questions];
        const questionIndex = newQuestions.findIndex((q) => q.code === queCode);
        const newSelections = [...newQuestions[questionIndex].selections];
        let selectionCount = newSelections.length;
        countryNames.forEach((countryName) => {
          const alreadyExists = newSelections.some((sel) => sel.text === countryName);
          if (!alreadyExists) {
            selectionCount += 1;
            newSelections.push({
              text: countryName,
              active: true,
              value: selectionCount.toString(),
              showOrder: selectionCount,
              code: `${queCode}_S${selectionCount}`,
              translations: [],
            });
          }
        });
        newQuestions[questionIndex] = { ...newQuestions[questionIndex], selections: newSelections };
        newSections[sectionIndex] = { ...newSections[sectionIndex], questions: newQuestions };
        return { ...prevState, sections: newSections };
      });
    };

    const countrySaveHandler = (arrCountries) => {
      addCountriesAsOptions(que.code, arrCountries);
    };

    const selectedCountryOptions = que.selections
      .filter((sel) => countryOptions.includes(sel.text))
      .map((sel) => sel.text);


  const optChangeHandler = (e, queCode, selCode) => {
    setError({ show: false, message: "" });
    setPayloadData((prevState) => {
      const sectionIndex = prevState.sections.findIndex((section) => section.code === selectedSec);
      const newSections = [...prevState.sections];
      const newQuestions = [...newSections[sectionIndex].questions];
      const questionIndex = newQuestions.findIndex((que) => que.code === queCode);
      const newSelections = [...newQuestions[questionIndex].selections];
      const selectionIndex = newSelections.findIndex((sel) => sel.code === selCode);
      newSelections[selectionIndex].text = e.target.value;
      newQuestions[questionIndex] = { ...newQuestions[questionIndex], selections: newSelections };
      newSections[sectionIndex] = { ...newSections[sectionIndex], questions: newQuestions };
      return { ...prevState, sections: newSections };
    });
  };

  const optDeleteHandler = (que, sel) => {
    setPayloadData((prevState) => {
      const sectionIndex = prevState.sections.findIndex((section) => section.code === selectedSec);
      const newSections = [...prevState.sections];
      const newQuestions = [...newSections[sectionIndex].questions];
      const questionIndex = newQuestions.findIndex((nq) => nq.code === que.code);
      const newSelections = [...newQuestions[questionIndex].selections];
      const index = newSelections.findIndex((ns) => ns.code === sel.code);
      if (index > -1) newSelections.splice(index, 1);
      newSelections.forEach((ns) => {
        if (ns.showOrder > sel.showOrder) {
          const newCount = ns.showOrder - 1;
          ns.showOrder = newCount;
          ns.code = ns.code.split("_")[0] + "_" + ns.code.split("_")[1] + "_" + ns.code.split("_")[2] + "_S" + newCount;
          ns.value = newCount.toString();
        }
      });
      newQuestions[questionIndex] = { ...newQuestions[questionIndex], selections: newSelections };
      newSections[sectionIndex] = { ...newSections[sectionIndex], questions: newQuestions };
      return { ...prevState, sections: newSections };
    });
  };

  const addOptHandler = (queCode) => {
    setError({ show: false, message: "" });
    let objNewOption = {
      text: "",
      active: true,
      value: "",
      showOrder: 0,
      code: queCode + "_S",
      translations:[]
    };
    setPayloadData((prevState) => {
      const sectionIndex = prevState.sections.findIndex((section) => section.code === selectedSec);
      const newSections = [...prevState.sections];
      const newQuestions = [...newSections[sectionIndex].questions];
      const questionIndex = newQuestions.findIndex((que) => que.code === queCode);
      const selectionCount = newQuestions[questionIndex].selections.length + 1;
      objNewOption.showOrder = selectionCount;
      objNewOption.code = objNewOption.code + selectionCount;
      objNewOption.value = selectionCount.toString();
      const newSelections = [...newQuestions[questionIndex].selections, objNewOption];
      newQuestions[questionIndex] = { ...newQuestions[questionIndex], selections: newSelections };
      newSections[sectionIndex] = { ...newSections[sectionIndex], questions: newQuestions };
      return { ...prevState, sections: newSections };
    });
  };

  return (
    <>
      <Grid container mt={4} spacing={4} mb={4}>
        {que.selections.length > 0 && (
          <>
            <Grid item xs={9}>
              <FormLabel sx={{ fontSize: "medium" }}>Text</FormLabel>
            </Grid>
            <Grid item xs={3} textAlign="center">
              <FormLabel sx={{ fontSize: "medium" }}>Delete</FormLabel>
            </Grid>
          </>
        )}
        {que.selections
          .sort((a, b) => (a.showOrder ?? 0) - (b.showOrder ?? 0))
          .map((sel) => {
            return (
              <Grid container key={sel.code} item spacing={2}>
                <Grid item xs={9}>
                  <TextField required placeholder="Please enter the selection text here..." name="selectionText" onChange={(e) => optChangeHandler(e, que.code, sel.code)} value={sel.text} />
                </Grid>
                <Grid item xs={3} textAlign="center" alignContent="center">
                  <IconButton onClick={() => optDeleteHandler(que, sel)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            );
          })}
      </Grid>
      <Button variant="primary" onClick={() => addOptHandler(que.code)}>
        Add Option
      </Button>

      <Button sx={{ ml: 2 }} endIcon={<KeyboardArrowDownIcon />} variant="primary" onClick={handleClick} >
        Load From
      </Button>
      <Menu
        anchorEl={anchorEl}
        onClose={handleClose}
        open={Boolean(anchorEl)}
      >
        <MenuItem onClick={addCountryOptions}>{'Country'}</MenuItem>        
      </Menu>
      <ModalSelectCountry
        show={showCountryModal}
        handleClose={() => setShowCountryModal(false)}
        countryList={countryOptions}
        selectedCountries={selectedCountryOptions}
        onAdd={countrySaveHandler}
        title="Select Countries"
      />
    </>
  );
};

export default SelectionsTab;
